/**
 * FloatingChatButton Component
 * 
 * Always visible floating chat button
 * Desktop: Orange color with comments icon (important CTA)
 * Mobile: WhatsApp colors and logo
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ProIcon } from './ProIcon';
import { useLocation } from 'react-router-dom';
import { buildWhatsAppUrl } from '../config/company';
import { trackContactClick } from '../lib/analytics';
import { useSearchBar } from '../contexts/SearchBarContext';
import { useModal } from '../contexts/ModalContext';

export const FloatingChatButton = () => {
  const location = useLocation();
  const { isHeroSearchVisible } = useSearchBar();
  const { isAnyModalOpen } = useModal();
  // Desktop: >=1024 (lg), Tablet: 768-1023 (md), Mobile: <768
  const [screenSize, setScreenSize] = useState<'mobile' | 'tablet' | 'desktop'>(
    typeof window !== 'undefined'
      ? window.innerWidth >= 1024 ? 'desktop' : window.innerWidth >= 768 ? 'tablet' : 'mobile'
      : 'desktop'
  );
  const [hasVisibleBookingForm, setHasVisibleBookingForm] = useState(false);
  const [isStickyBarVisible, setIsStickyBarVisible] = useState(false);
  const rafRef = useRef<number | null>(null);

  const isHomePage = location.pathname === '/';
  const isPropertyPage = location.pathname.startsWith('/detail/') || location.pathname.startsWith('/property/');

  const shouldShowMobileChat = useMemo(() => {
    if (screenSize !== 'mobile') return false;
    if (isAnyModalOpen) return false;
    
    // Home page: show only when scrolled down (search not visible)
    if (isHomePage && isHeroSearchVisible) return false;
    
    // Property page: only show WhatsApp when large booking form is visible
    if (isPropertyPage) {
      return hasVisibleBookingForm;
    }
    
    return true;
  }, [screenSize, isAnyModalOpen, isHomePage, isPropertyPage, isHeroSearchVisible, hasVisibleBookingForm]);

  // Desktop only (>=1024px) — hide on tablet entirely
  const shouldShowDesktopChat = useMemo(() => {
    if (screenSize !== 'desktop') return false;
    if (isAnyModalOpen) return false;
    if (isHomePage && isHeroSearchVisible) return false;
    // Always show on desktop (even when booking form is visible) — just position above sticky bar
    return true;
  }, [screenSize, isAnyModalOpen, isHomePage, isHeroSearchVisible]);

  useEffect(() => {
    const handleResize = () => {
      const w = window.innerWidth;
      setScreenSize(w >= 1024 ? 'desktop' : w >= 768 ? 'tablet' : 'mobile');
    };
    window.addEventListener('resize', handleResize, { passive: true });
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;

    const computeVisibility = () => {
      // Check large booking form visibility (data-chat-blocker="booking-cta")
      const formBlockers = document.querySelectorAll<HTMLElement>('[data-chat-blocker="booking-cta"]');
      const hasFormVisible = Array.from(formBlockers).some((el) => {
        const style = window.getComputedStyle(el);
        if (style.display === 'none' || style.visibility === 'hidden' || parseFloat(style.opacity) === 0) return false;
        const rect = el.getBoundingClientRect();
        return rect.width > 0 && rect.height > 0 && rect.bottom > 0 && rect.top < window.innerHeight;
      });
      setHasVisibleBookingForm(hasFormVisible);

      // Check sticky bar visibility (data-chat-blocker="sticky-book-bar")
      const stickyBars = document.querySelectorAll<HTMLElement>('[data-chat-blocker="sticky-book-bar"]');
      const hasStickyVisible = Array.from(stickyBars).some((el) => {
        const style = window.getComputedStyle(el);
        if (style.display === 'none' || style.visibility === 'hidden' || parseFloat(style.opacity) === 0) return false;
        const rect = el.getBoundingClientRect();
        return rect.width > 0 && rect.height > 0 && rect.bottom > 0 && rect.top < window.innerHeight;
      });
      setIsStickyBarVisible(hasStickyVisible);
    };

    const scheduleComputeVisibility = () => {
      if (rafRef.current !== null) {
        window.cancelAnimationFrame(rafRef.current);
      }
      rafRef.current = window.requestAnimationFrame(() => {
        rafRef.current = null;
        computeVisibility();
      });
    };

    const intersectionObserver = new IntersectionObserver(() => {
      scheduleComputeVisibility();
    }, { threshold: [0, 0.05, 0.25] });

    const observeCurrentBlockers = () => {
      intersectionObserver.disconnect();
      const all = document.querySelectorAll<HTMLElement>('[data-chat-blocker]');
      all.forEach((el) => intersectionObserver.observe(el));
      scheduleComputeVisibility();
    };

    const mutationObserver = new MutationObserver(() => {
      observeCurrentBlockers();
    });

    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style', 'data-chat-blocker'],
    });

    window.addEventListener('scroll', scheduleComputeVisibility, { passive: true });
    window.addEventListener('resize', scheduleComputeVisibility, { passive: true });

    observeCurrentBlockers();

    return () => {
      intersectionObserver.disconnect();
      mutationObserver.disconnect();
      window.removeEventListener('scroll', scheduleComputeVisibility);
      window.removeEventListener('resize', scheduleComputeVisibility);
      if (rafRef.current !== null) {
        window.cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [location.pathname]);

  const handleClick = () => {
    trackContactClick('chat', 'floating_button');
    
    // Use WhatsApp on mobile, online chat on desktop
    if (window.innerWidth < 768) {
      // Open WhatsApp with pre-filled message
      window.open(buildWhatsAppUrl('Hi SUNMARBELLA! I need help with my booking.'), '_blank');
    } else {
      // Open online chat service on desktop
      window.open('https://tawk.to/chat/your-property-id', '_blank', 'width=400,height=600');
    }
  };

  if (typeof document === 'undefined' || !document.body) return null;

  // Position chat button above sticky bar when it's visible
  const desktopBottom = isStickyBarVisible
    ? 'bottom-[calc(5rem+env(safe-area-inset-bottom,0px))]'
    : 'bottom-[calc(1.5rem+env(safe-area-inset-bottom,0px))]';

  return createPortal(
    <>
      {/* Desktop only (>=1024px) - positioned above sticky bar */}
      {shouldShowDesktopChat && (
        <button
          onClick={handleClick}
          className={`hidden lg:flex fixed ${desktopBottom} right-6 z-[9997] items-center gap-3 px-5 py-4 bg-brand-sun hover:bg-brand-sun-dark text-white rounded-full shadow-2xl shadow-brand-sun/30 transition-all hover:scale-105 active:scale-95 group`}
          aria-label="Chat with us"
          title="Chat with us"
        >
          <div className="relative">
            <ProIcon icon="comments" family="light" className="w-6 h-6" />
            <span className="absolute -top-1 -right-1 flex h-3 w-3">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
            </span>
          </div>
          <span className="font-bold text-sm whitespace-nowrap">Chat with us</span>
        </button>
      )}

      {/* Mobile only (<768px) - WhatsApp when large booking form is visible */}
      {shouldShowMobileChat && (
        <button
          onClick={handleClick}
          className="lg:hidden md:hidden fixed bottom-[calc(1.5rem+env(safe-area-inset-bottom,0px))] right-5 z-[9997] w-16 h-16 bg-[#25D366] hover:bg-[#128C7E] text-white rounded-full shadow-2xl shadow-[#25D366]/30 transition-all hover:scale-105 active:scale-95 flex items-center justify-center"
          aria-label="Chat on WhatsApp"
          title="Chat on WhatsApp"
        >
          <ProIcon icon="whatsapp" family="brands" className="text-4xl" />
        </button>
      )}
    </>,
    document.body
  );
};
